// src/app/services/prompt-tracking-api.ts
// Real API calls — all mock data removed.
// Follows the same apiFetch pattern used across the codebase.

import {
  apiFetch,
  AuthExpiredError,
  getStoredToken,
  notifyAuthExpired,
} from '../../../api/api-fetch';
import type {
  BriefingInsight,
  CreatePromptPayload,
  Prompt,
  PromptFilters,
  PromptSummaryStats,
  PromptsDataResponse,
  UpdatePromptPayload,
} from '../../types/prompt-tracking-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.append(k, v);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

function getBrandName(): string {
  return (
    localStorage.getItem('bm_brand') ||
    sessionStorage.getItem('bm_brand') ||
    'default'
  );
}

// ---------------------------------------------------------------------------
// Fetch all prompts with optional filtering
// ---------------------------------------------------------------------------

export async function fetchPrompts(
  filters: PromptFilters = {}
): Promise<PromptsDataResponse> {
  const qs = buildQuery({
    brand_name: filters.brandName ?? getBrandName(),
    intent: filters.intent && filters.intent !== 'all' ? filters.intent : undefined,
    status: filters.status && filters.status !== 'all' ? filters.status : undefined,
    search: filters.searchQuery?.trim() || undefined,
  });

  const res = await apiFetch<PromptsDataResponse>(`/prompt-tracking/prompts${qs}`);
  return res;
}

// ---------------------------------------------------------------------------
// Fetch a single prompt by ID
// ---------------------------------------------------------------------------

export async function fetchPromptById(id: string): Promise<Prompt | null> {
  try {
    return await apiFetch<Prompt>(`/prompt-tracking/prompts/${id}`);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Fetch summary stats only
// ---------------------------------------------------------------------------

export async function fetchPromptSummary(): Promise<PromptSummaryStats> {
  const qs = buildQuery({ brand_name: getBrandName() });
  return apiFetch<PromptSummaryStats>(`/prompt-tracking/summary${qs}`);
}

// ---------------------------------------------------------------------------
// Create a new prompt
// ---------------------------------------------------------------------------

export async function createPrompt(data: CreatePromptPayload): Promise<Prompt> {
  return apiFetch<Prompt>('/prompt-tracking/prompts', {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      brand_name: data.brand_name || getBrandName(),
    }),
  });
}

// ---------------------------------------------------------------------------
// Update a prompt
// ---------------------------------------------------------------------------

export async function updatePrompt(
  id: string,
  updates: UpdatePromptPayload
): Promise<Prompt | null> {
  try {
    return await apiFetch<Prompt>(`/prompt-tracking/prompts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Delete a prompt
// ---------------------------------------------------------------------------

export async function deletePrompt(id: string): Promise<{ success: boolean }> {
  try {
    return await apiFetch<{ success: boolean }>(
      `/prompt-tracking/prompts/${id}`,
      { method: 'DELETE' }
    );
  } catch {
    return { success: false };
  }
}

// ---------------------------------------------------------------------------
// Toggle pause/resume
// ---------------------------------------------------------------------------

export async function togglePromptPause(
  id: string,
  paused: boolean
): Promise<Prompt | null> {
  try {
    return await apiFetch<Prompt>(`/prompt-tracking/prompts/${id}/pause`, {
      method: 'POST',
      body: JSON.stringify({ paused }),
    });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Fetch AI briefing insights
// ---------------------------------------------------------------------------

export async function getBriefingInsights(
  forceRefresh = false
): Promise<BriefingInsight[]> {
  const qs = buildQuery({
    brand_name: getBrandName(),
    force_refresh: forceRefresh ? 'true' : undefined,
  });

  const res = await apiFetch<{ insights: BriefingInsight[] }>(
    `/prompt-tracking/briefing-insights${qs}`
  );
  return res.insights ?? [];
}

// ---------------------------------------------------------------------------
// WebSocket: stream real-time analysis for a single prompt
// ---------------------------------------------------------------------------

export interface AnalysisProgressEvent {
  type: 'analysis_started' | 'progress' | 'final_result' | 'error';
  done?: number;
  total?: number;
  percent?: number;
  data?: Partial<Prompt>;
  message?: string;
}

export function openPromptAnalysisSocket(
  params: {
    promptId: string;
    promptText: string;
    intent: string;
    brandName?: string;
    models: string[];
  },
  onEvent: (event: AnalysisProgressEvent) => void
): () => void {
  const token = getStoredToken();
  if (!token) {
    notifyAuthExpired('/api/ws/prompt-analysis');
    throw new AuthExpiredError('/api/ws/prompt-analysis');
  }

  const baseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(
    /^http/,
    'ws'
  );
  const ws = new WebSocket(
    `${baseUrl}/api/ws/prompt-analysis?token=${encodeURIComponent(token)}`,
  );

  ws.onopen = () => {
    ws.send(
      JSON.stringify({
        promptId: params.promptId,
        promptText: params.promptText,
        intent: params.intent,
        brandName: params.brandName ?? getBrandName(),
        models: params.models,
      })
    );
  };

  ws.onmessage = (e) => {
    try {
      const evt: AnalysisProgressEvent = JSON.parse(e.data);
      onEvent(evt);
    } catch {
      // ignore malformed frames
    }
  };

  ws.onerror = () => {
    onEvent({ type: 'error', message: 'WebSocket connection error' });
  };

  // Return cleanup function
  return () => {
    if (ws.readyState === WebSocket.OPEN) ws.close();
  };
}